import React, { useMemo, useState } from 'react';
import { useTranslate, SimpleForm, useNotify, useQuery, GET_MANY_REFERENCE, RecordContextProvider } from 'react-admin';
import { Dialog, Button, CircularProgress, DialogActions, ListItemIcon, ListItemText, DialogTitle, makeStyles } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Category } from '@material-ui/icons';
import arrayMutators from 'final-form-arrays'
import { useFormState } from 'react-final-form';

import { ASSIGNMENT_POSITIONS } from '@hisports/scoresheet/src/constants';

import { ActionMenuItem } from '../../../common/ActionMenu';
import { UpdatedAtField } from '../../../common/fields/UpdatedAtField';
import { useIsOwnParticipant } from '../../../common/hooks/hooks';
import { apiClient, useHttpClient, useSport } from '../../../http';
import { SelectableCategoriesInput } from '../../categories/SelectableCategoriesInput';

const useStyles = makeStyles((theme) => ({
  updatedAt: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  alert: {
    marginBottom: theme.spacing(1),
  },
  group: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  }
}));

const UpdatedAt = ({ officialCategories }) => {
  const classes = useStyles();

  const latestCategory = useMemo(() => {
    return officialCategories.reduce((latest, category) => {
      if (!category.updatedAt) return latest;
      if (!latest || new Date(category.updatedAt) > new Date(latest.updatedAt)) {
        return category;
      }
      return latest;
    }, null);
  }, [officialCategories]);

  if (!latestCategory) return null;
  return <div className={classes.updatedAt}>
    <RecordContextProvider value={latestCategory}>
      <UpdatedAtField includePrefix typographyProps={{ variant: 'caption', color: 'textSecondary' }} />
    </RecordContextProvider>
  </div>
}

const OfficialCategoriesAlert = ({ allCategoriesTotal = 0 }) => {
  const translate = useTranslate();
  const classes = useStyles();
  const { values } = useFormState();

  const availableToAll = Object.values(values).every(value => value.length === allCategoriesTotal);

  if (!availableToAll) return null
  return <Alert severity="success" className={classes.alert}>
    <AlertTitle>{translate('resources.participants.labels.availability.categories_availabilities.alerts.available_to_all_title')}</AlertTitle>
    {translate('resources.participants.labels.availability.categories_availabilities.alerts.available_to_all_body')}
  </Alert>
}

const Toolbar = ({ handleSubmit, onClose }) => {
  const translate = useTranslate();
  const { submitting } = useFormState();

  const submitLabel = submitting ? 'resources.participants.labels.availability.saving' : 'ra.action.save';

  return <DialogActions>
    <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>
    <Button type="submit" color="primary" onClick={handleSubmit} disabled={submitting}>
      {translate(submitLabel)}
    </Button>
  </DialogActions>
}

const OfficialCategoryEdit = ({ allCategories = [], officialCategories }) => {
  const sport = useSport();
  const translate = useTranslate();

  return <>
    {ASSIGNMENT_POSITIONS
      .filter(({ sports }) => !sports || sports.includes(sport))
      .map(({ position }) => {
        return <SelectableCategoriesInput
          label={translate(`resources.games.values.assignment.position.${position}`)}
          source={position}
          categories={allCategories}
        />
      })}
    <UpdatedAt officialCategories={officialCategories} />
  </>
}

const toFormValues = (sport, allCategoryIds = [], officialCategories = []) => {
  const formValues = {};

  ASSIGNMENT_POSITIONS
    .filter(({ sports }) => !sports || sports.includes(sport))
    .map(({ position }) => {
      const invalidCategories = (officialCategories || []).find(({ position: p }) => p === position)?.categoryIds || [];
      const validCategories = (allCategoryIds || []).filter(categoryId => !invalidCategories.includes(categoryId))

      formValues[position] = validCategories;
    });

  return formValues;
}

const toApiValues = (formValues, allCategoryIds = []) => {
  const apiValues = {};

  Object.keys(formValues).forEach(position => {
    const invalidCategories = (allCategoryIds || []).filter(category => !formValues[position].includes(category));
    apiValues[position] = invalidCategories;
  })

  return apiValues;
}

const OfficialCategoriesForm = ({ onClose, participantId }) => {
  const notify = useNotify();
  const translate = useTranslate();
  const sport = useSport();

  const { data: allCategories = [], loaded: categoriesLoaded, error: categoriesError } = useHttpClient('/categories');
  const { data: officialCategories = [], loaded: officialCategoriesLoaded, error: officialCategoriesError } = useQuery({
    type: GET_MANY_REFERENCE,
    resource: 'officialCategories',
    payload: {
      target: 'participants',
      id: participantId,
      pagination: { page: 1, perPage: 999 },
      sort: { field: 'position', order: 'ASC' },
      filter: {}
    },
  }, { enabled: participantId != null });

  const error = categoriesError || officialCategoriesError;
  const loaded = categoriesLoaded && officialCategoriesLoaded;

  const allCategoryIds = useMemo(() => (allCategories || []).map(category => category.id), [allCategories]);
  const initialValues = useMemo(() => {
    return toFormValues(sport, allCategoryIds, officialCategories);
  }, [allCategoryIds, officialCategories, sport]);

  const handleSave = async values => {
    try {
      await apiClient(`/participants/${participantId}/officialSettings`, {
        method: 'PATCH',
        data: {
          positionCategories: toApiValues(values, allCategoryIds),
        },
      })

      notify('resources.participants.labels.availability.categories_updated', 'success');
      onClose();
    } catch (error) {
      notify('resources.participants.labels.availability.categories_error', 'error');
    }
  }

  if (error) {
    return <Alert severity="error">{translate('ra.page.error_try_again')}</Alert>
  }

  if (!loaded) {
    return <Alert severity="info" icon={<CircularProgress color="secondary" size={22} />}>{translate('ra.page.loading')}</Alert>
  }

  return <RecordContextProvider value={initialValues}>
    <SimpleForm initialValues={initialValues} mutators={arrayMutators} toolbar={<Toolbar onClose={onClose} />} fullWidth save={handleSave}>
      <OfficialCategoriesAlert onClose={onClose} allCategoriesTotal={allCategories?.length} />
      <OfficialCategoryEdit allCategories={allCategories} officialCategories={officialCategories} />
    </SimpleForm>
  </RecordContextProvider>
}

const OfficialCategoriesDialog = ({ isOpen, handleClose, participantId }) => {
  const translate = useTranslate();
  const isOwnParticipant = useIsOwnParticipant(participantId);

  return <Dialog
    maxWidth="lg"
    fullWidth
    open={isOpen}
  >
    <DialogTitle>{translate(`resources.participants.labels.availability.categories_availabilities.${isOwnParticipant ? 'own_profile' : 'other_profile'}.title`)}</DialogTitle>
    <OfficialCategoriesForm onClose={handleClose} participantId={participantId} />
  </Dialog>
}

export const OfficialCategoriesMenuItem = ({ participantId, ...props }) => {
  const translate = useTranslate();
  const [isOpen, setOpen] = useState(false);
  const isOwnParticipant = useIsOwnParticipant(participantId);

  return <>
    <ActionMenuItem color="primary" size="small" onClick={() => setOpen(true)}>
      <ListItemIcon><Category fontSize="small" /></ListItemIcon>
      <ListItemText>{translate(`resources.participants.labels.availability.categories_availabilities.${isOwnParticipant ? 'own_profile' : 'other_profile'}.title`)}</ListItemText>
    </ActionMenuItem>
    {isOpen && <OfficialCategoriesDialog isOpen={isOpen} handleClose={() => setOpen(false)} participantId={participantId} {...props} />}
  </>
}
